/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Attention-grabbing one-liners for the home page agent tile. One is picked at
 * random per page load: the tile is competing with every other widget on the
 * landing page, and a line that changes is a line people keep reading.
 *
 * Rules for anything added here: short enough to fit one line in the tile,
 * cheeky at the agent's own expense (never the user's), and honest about what
 * the agent can actually do — see agentQuickStartActions.ts for the real list.
 */
export interface AgentHook {
  phrase: string;
  emoji: string;
}

export const agentHooks: readonly AgentHook[] = [
  { phrase: "I'm smarter than you, let me prove it", emoji: '😈' },
  { phrase: "I'm smart and can do things — what are you waiting for?", emoji: '🚀' },
  { phrase: 'Your configs have secrets. I have opinions.', emoji: '🕵️' },
  { phrase: 'I read every row so you never have to', emoji: '📖' },
  { phrase: 'Give me a run. I will find the weird part.', emoji: '🔍' },
  { phrase: 'I do spreadsheets in my sleep. I never sleep.', emoji: '🧮' },
  { phrase: 'Ask me the question you were going to Slack someone about', emoji: '💬' },
  { phrase: 'Faster than a meeting, cheaper than a consultant', emoji: '⚡' },
  { phrase: 'I will sanity-check that before your reviewer does', emoji: '🧐' },
  { phrase: 'One prompt. Fifty runs. Zero complaints.', emoji: '🏭' },
  { phrase: 'Bad assumptions die here', emoji: '⚰️' },
  { phrase: 'I speak fluent PVSim', emoji: '🗣️' },
  { phrase: 'Your losses, itemized and shamed', emoji: '📉' },
  { phrase: 'I have read the docs. All of them.', emoji: '🤓' },
  { phrase: 'Point me at a number and ask me why', emoji: '❓' },
  { phrase: 'I write the Python so you can keep the credit', emoji: '🐍' },
  { phrase: 'Suspiciously good at finding outliers', emoji: '🎯' },
  { phrase: 'Try me. Worst case, you learn something.', emoji: '🎲' },
  { phrase: 'I never get bored of monthly energy tables', emoji: '📊' },
  { phrase: 'Explain-like-I-am-new-here mode: always on', emoji: '🧑‍🎓' },
  { phrase: 'Let me do the boring 80%', emoji: '🧹' },
  { phrase: 'I can compare two configs before your coffee lands', emoji: '☕' },
  { phrase: 'Every gut feeling deserves a second opinion', emoji: '🫀' },
  { phrase: 'I turn "looks fine" into "here is why"', emoji: '🔬' },
  { phrase: 'Nobody checks the transformer. I check the transformer.', emoji: '🔌' },
  { phrase: 'Ask me something hard. Please.', emoji: '🥱' },
  { phrase: 'I plot things. Beautiful things.', emoji: '📈' },
  { phrase: 'Your future self already asked me this', emoji: '⏳' },
  { phrase: 'Warning: may improve your yield estimate', emoji: '⚠️' },
  { phrase: 'I do not guess. I query.', emoji: '🗄️' },
  { phrase: 'Two clicks from question to chart', emoji: '🖱️' },
  { phrase: 'I have no ego about being wrong. Test me.', emoji: '🤝' },
  { phrase: 'Somebody has to read the weather file', emoji: '🌦️' },
  { phrase: 'I found something in your last run. Curious?', emoji: '👀' },
  { phrase: 'Batch runs, ranked, while you read this', emoji: '🏁' },
  { phrase: 'The most patient colleague you will ever have', emoji: '🧘' },
  { phrase: 'Tell me what you are trying to prove', emoji: '🧪' },
  { phrase: 'I make sensitivity analysis look easy', emoji: '🎚️' },
  { phrase: 'Undocumented assumption? Not on my watch.', emoji: '🚨' },
  { phrase: 'Let us go find a bug in your inputs', emoji: '🐛' },
  { phrase: 'I read the whole database so you can skim', emoji: '⚡' },
  { phrase: 'Zero judgement, infinite follow-up questions', emoji: '♾️' },
  { phrase: 'Costing assumptions: reviewed in seconds', emoji: '💸' },
  { phrase: 'I am basically a second analyst who never takes leave', emoji: '🏖️' },
  { phrase: 'Ask me why the numbers moved', emoji: '🎢' },
  { phrase: 'Your last run and I need to talk', emoji: '📞' },
  { phrase: 'Best used before you send that deck', emoji: '📤' },
  { phrase: 'I will happily disagree with your model', emoji: '🥊' },
  { phrase: 'Curiosity costs one click', emoji: '🔓' },
  { phrase: 'Still here. Still faster than doing it by hand.', emoji: '⏱️' },
];

const shuffledIndex = (length: number, random: () => number) =>
  Math.min(length - 1, Math.floor(random() * length));

/** Picks one hook at random. `random` is injectable so tests stay deterministic. */
export function pickAgentHook(random: () => number = Math.random): AgentHook {
  return agentHooks[shuffledIndex(agentHooks.length, random)];
}

export const formatAgentHook = ({ phrase, emoji }: AgentHook) => `${phrase} ${emoji}`;
